import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { MaintenanceOrder, MaintenancePlan, OrdersListResponse, PlansListResponse } from './types';

const KEY = {
  plans: (p: Record<string, unknown>) => ['maintenance-plans', p] as const,
  orders: (p: Record<string, unknown>) => ['maintenance-orders', p] as const,
  dashboard: ['maintenance-dashboard'] as const,
};

// Plans
export function useMaintenancePlans(params: { page?: number; limit?: number; vehicleId?: string; type?: string } = {}) {
  return useQuery<PlansListResponse>({
    queryKey: KEY.plans(params),
    queryFn: async () => {
      const { data } = await api.get<PlansListResponse>('/maintenance/plans', { params });
      return data;
    },
  });
}

export function useCreateMaintenancePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { data } = await api.post<{ data: MaintenancePlan }>('/maintenance/plans', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-plans'] }),
  });
}

export function useDeleteMaintenancePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/maintenance/plans/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-plans'] }),
  });
}

// Orders
export function useMaintenanceOrders(params: { page?: number; limit?: number; vehicleId?: string; status?: string; type?: string } = {}) {
  return useQuery<OrdersListResponse>({
    queryKey: KEY.orders(params),
    queryFn: async () => {
      const { data } = await api.get<OrdersListResponse>('/maintenance/orders', { params });
      return data;
    },
  });
}

export function useCreateMaintenanceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { data } = await api.post<{ data: MaintenanceOrder }>('/maintenance/orders', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-orders'] }),
  });
}

export function useOrderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'approve' | 'start' | 'complete' | 'cancel' }) => {
      const { data } = await api.post<{ data: MaintenanceOrder }>(`/maintenance/orders/${id}/${action}`);
      return data.data;
    },
    onSuccess: () => {
      // Invalida orders, plans e dashboard (plano pode ter sido recalculado)
      qc.invalidateQueries({ queryKey: ['maintenance-orders'] });
      qc.invalidateQueries({ queryKey: ['maintenance-plans'] });
      qc.invalidateQueries({ queryKey: KEY.dashboard });
    },
  });
}

export function useDeleteMaintenanceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/maintenance/orders/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-orders'] }),
  });
}

// Dashboard
export function useMaintenanceDashboard() {
  return useQuery({
    queryKey: KEY.dashboard,
    queryFn: async () => {
      const { data } = await api.get<{ data: Record<string, unknown> }>('/maintenance/dashboard');
      return data.data;
    },
  });
}
